import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { RunnerTargetSelectComponent } from '../../../components/runner-target-select/runner-target-select.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { ObjectCaptureService } from '../../../services/object-capture.service';
import type { RunTarget } from '../../../services/execution-job.service';
import { ObjectMappingSummary } from '../../../models/object-mapping.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { isValidObjectName } from '../object-names';
import { isNameTaken, serverMessage } from '../server-message';

export type CreateWay = 'empty' | 'record' | 'tox';

/** Anleitung des Classic-Importers. Leer, bis es eine oeffentliche Seite gibt; dann zeigt der Dialog den Link. */
export const CLASSIC_IMPORTER_GUIDE_URL = '';

const RECORD_ERRORS: Readonly<Record<string, string>> = {
  RUNNER_OFFLINE: 'objectMappings.record.errors.runnerOffline',
  RUNNER_CANNOT_RECORD: 'objectMappings.record.errors.cannotRecord',
  RECORDING_ACTIVE: 'objectMappings.record.errors.recordingActive',
  ENGINE_NOT_SUPPORTED: 'objectMappings.record.errors.engineNotSupported',
};

function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Neue Objektbibliothek anlegen (NEX-1705): leer, per Aufnahme oder Hinweis auf den Classic-Importer
 * (NEX-1719). Mit `library` ist es der Dialog „Aufnehmen“ einer vorhandenen Bibliothek: ohne Name
 * und ohne Wege.
 *
 * Design-Luecke: Das Bundle hat keinen Entwurf dafuer. Aufbau wie der Umbenennen-Dialog, die Wege
 * als Auswahlkarten wie im ImportModal (`page-flows.jsx`), Vorlage ist der Klickdummy „Mapping aufnehmen“.
 */
@Component({
  selector: 'app-object-mapping-create-dialog',
  imports: [
    TranslatePipe,
    RouterLink,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatButton,
    MatIcon,
    ConfirmDialogComponent,
    RunnerTargetSelectComponent,
  ],
  templateUrl: './object-mapping-create-dialog.component.html',
  styleUrl: './object-mapping-create-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingCreateDialogComponent {
  private readonly service = inject(ObjectMappingService);
  private readonly captures = inject(ObjectCaptureService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  /** Gesetzt: Aufnahme fuer diese Bibliothek statt einer neuen. */
  readonly library = input<ObjectMappingSummary | null>(null);
  readonly created = output<ObjectMappingSummary>();
  readonly cancelled = output<void>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly guideUrl = CLASSIC_IMPORTER_GUIDE_URL;
  readonly ways: readonly { id: CreateWay; icon: string }[] = [
    { id: 'empty', icon: 'add' },
    { id: 'record', icon: 'radio_button_checked' },
    { id: 'tox', icon: 'upload_file' },
  ];

  readonly name = signal('');
  readonly way = signal<CreateWay>('empty');
  readonly target = signal<RunTarget | null>(null);
  readonly startUrl = signal('');
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  /** Laufende Aufnahme, wenn der Start an RECORDING_ACTIVE scheiterte. */
  readonly activeJobId = signal<string | null>(null);
  /**
   * Die schon angelegte Bibliothek, wenn danach der Start scheiterte. Ein neuer Versuch legt sie
   * nicht noch einmal an, er startet nur die Aufnahme.
   */
  readonly createdLibrary = signal<ObjectMappingSummary | null>(null);

  readonly recordOnly = computed(() => this.library() !== null);
  readonly recording = computed(() => this.recordOnly() || this.way() === 'record');
  readonly mapping = computed(() => this.library() ?? this.createdLibrary());
  readonly locked = computed(() => this.createdLibrary() !== null);

  readonly nameValid = computed(() => isValidObjectName(this.name()));
  readonly showInvalid = computed(() => this.name().length > 0 && !this.nameValid());
  readonly urlValid = computed(() => isHttpUrl(this.startUrl()));
  readonly showUrlInvalid = computed(() => this.startUrl().length > 0 && !this.urlValid());

  readonly canSubmit = computed(() => {
    if (this.saving()) return false;
    if (!this.recordOnly() && this.way() === 'tox') return true;
    const nameOk = this.mapping() !== null || this.nameValid();
    if (!this.recording()) return nameOk;
    return nameOk && this.target()?.kind === 'runner' && this.urlValid();
  });

  readonly title = computed(() => (this.recordOnly() ? 'objectMappings.record.title' : 'objectMappings.create.title'));
  readonly message = computed(() => {
    if (this.recordOnly()) return 'objectMappings.record.message';
    return this.way() === 'empty'
      ? 'objectMappings.create.message'
      : `objectMappings.create.ways.${this.way()}.message`;
  });
  readonly submitLabel = computed(() => {
    if (this.recording()) return 'objectMappings.record.submit';
    return this.way() === 'tox' ? 'objectMappings.create.ways.tox.close' : 'objectMappings.create.submit';
  });

  private readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private readonly urlInput = viewChild<ElementRef<HTMLInputElement>>('urlInput');
  private readonly runnerSelect = viewChild(RunnerTargetSelectComponent);

  constructor() {
    afterNextRender(() => (this.nameInput() ?? this.urlInput())?.nativeElement.focus());
  }

  onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
    this.error.set(null);
  }

  onUrlInput(event: Event): void {
    this.startUrl.set((event.target as HTMLInputElement).value);
  }

  chooseWay(way: CreateWay): void {
    this.way.set(way);
    this.error.set(null);
  }

  submit(): void {
    if (!this.canSubmit()) return;
    if (!this.recordOnly() && this.way() === 'tox') {
      this.cancelled.emit();
      return;
    }
    const mapping = this.mapping();
    if (mapping !== null) {
      this.startRecording(mapping);
      return;
    }
    this.create();
  }

  /** Nach dem Anlegen ist die Liste ohne die neue Bibliothek veraltet; deshalb auch hier `created`. */
  cancel(): void {
    const created = this.createdLibrary();
    if (created === null) this.cancelled.emit();
    else this.created.emit(created);
  }

  toLibrary(): void {
    this.created.emit(this.createdLibrary()!);
  }

  private create(): void {
    this.saving.set(true);
    this.error.set(null);
    this.service
      .create(this.projectId(), { name: this.name() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (library) => {
          if (!this.recording()) {
            this.saving.set(false);
            this.created.emit(library);
            return;
          }
          this.createdLibrary.set(library);
          this.startRecording(library);
        },
        error: (err: unknown) => {
          this.error.set(this.createMessage(err));
          this.saving.set(false);
        },
      });
  }

  private startRecording(mapping: ObjectMappingSummary): void {
    const target = this.target();
    if (target?.kind !== 'runner') {
      this.saving.set(false);
      this.error.set(this.translate.instant('objectMappings.record.errors.chooseRunner'));
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.activeJobId.set(null);
    this.captures
      .startRecording(this.projectId(), mapping.id, target.runnerId, this.startUrl())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          this.saving.set(false);
          void this.router.navigate([
            '/projects',
            this.projectId(),
            'object-mappings',
            mapping.id,
            'recording',
            job.id,
          ]);
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.showStartError(err);
        },
      });
  }

  private showStartError(err: unknown): void {
    const body = err instanceof HttpErrorResponse ? (err.error as { code?: unknown; jobId?: unknown } | null) : null;
    const key = typeof body?.code === 'string' ? RECORD_ERRORS[body.code] : undefined;
    if (key) {
      this.error.set(this.translate.instant(key));
      if (body?.code === 'RECORDING_ACTIVE' && typeof body.jobId === 'string') this.activeJobId.set(body.jobId);
      return;
    }
    if (err instanceof HttpErrorResponse && err.status === 404) {
      this.error.set(this.translate.instant('objectMappings.record.errors.runnerGone'));
      this.runnerSelect()?.loadRunners();
      return;
    }
    this.error.set(serverMessage(err) ?? this.translate.instant('objectMappings.record.errors.general'));
  }

  private createMessage(err: unknown): string {
    if (isNameTaken(err)) return this.translate.instant('objectMappings.create.nameTaken');
    return serverMessage(err) ?? this.translate.instant('objectMappings.create.error');
  }
}
